import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Building2, Save, Loader2, Copy, AlertCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { REGIMI_FISCALI, ITALIAN_PROVINCES, COMMON_COMUNI } from "@/lib/fiscal-constants";

const EMPTY = {
  denominazione: "", partita_iva: "", codice_fiscale: "", regime_fiscale: "RF01",
  indirizzo: "", cap: "", comune: "", provincia: "", nazione: "IT",
  telefono: "", email_pec: "", codice_destinatario: "0000000",
  suffisso_numerazione: "", aliquota_iva_default: "",
  sezionale: "AF01", next_number_by_year: {},
};

function isValidPiva(s) {
  return /^[0-9]{11}$/.test((s || "").trim());
}
function isValidCf(s) {
  if (!s) return false;
  const v = s.trim().toUpperCase();
  return /^[0-9]{11}$/.test(v) || /^[A-Z]{6}[0-9]{2}[A-Z][0-9]{2}[A-Z][0-9]{3}[A-Z]$/.test(v);
}
function isValidCap(s) {
  return /^[0-9]{5}$/.test((s || "").trim());
}

export default function CompanyProfile() {
  const { user } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const isCommercialista = !!user?.is_commercialista;

  const load = async () => {
    try {
      const { data } = await api.get("/company");
      setForm({ ...EMPTY, ...data, nazione: "IT", codice_destinatario: (data.codice_destinatario && data.codice_destinatario.trim()) || "0000000" });
    } catch {
      toast.error("Errore nel caricamento profilo");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const errors = {
    denominazione: !form.denominazione?.trim() ? "Campo obbligatorio" : "",
    partita_iva: !isValidPiva(form.partita_iva) ? "Deve essere di 11 cifre" : "",
    codice_fiscale: !isValidCf(form.codice_fiscale) ? "CF non valido (11 cifre o 16 caratteri)" : "",
    indirizzo: !form.indirizzo?.trim() ? "Campo obbligatorio" : "",
    cap: !isValidCap(form.cap) ? "CAP deve essere di 5 cifre" : "",
    comune: !form.comune?.trim() ? "Campo obbligatorio" : "",
    provincia: !form.provincia?.trim() ? "Seleziona una provincia" : "",
  };
  const hasErrors = Object.values(errors).some(Boolean);

  const save = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      let payload;
      if (isCommercialista) {
        // Full edit allowed: send the entire form.
        payload = { ...form, nazione: "IT" };
      } else {
        // Regular users: only the whitelisted fields.
        payload = {
          email_pec: form.email_pec || "",
          codice_destinatario: (form.codice_destinatario || "").trim() || "0000000",
          sezionale: form.sezionale || "AF01",
          next_number_by_year: form.next_number_by_year || {},
        };
      }
      if (!payload.codice_destinatario || !payload.codice_destinatario.trim()) {
        payload.codice_destinatario = "0000000";
      }
      await api.put("/company", payload);
      toast.success(isCommercialista ? "Profilo cliente salvato" : "Impostazioni salvate");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const copyEmail = () => {
    navigator.clipboard.writeText(form?.inbound_email || "");
    toast.success("Email copiata");
  };

  const showErr = (k) => submitAttempted && errors[k];

  if (loading) {
    return <div className="p-10"><Loader2 className="w-6 h-6 animate-spin text-zinc-500" /></div>;
  }

  return (
    <div className="p-4 sm:p-6 lg:p-10" data-testid="company-page">
      {/* Datalist for comune autocomplete */}
      <datalist id="comuni-datalist">
        {COMMON_COMUNI.map((c) => <option key={c} value={c} />)}
      </datalist>

      <div className="mb-8 flex items-center gap-3">
        <div className="w-11 h-11 rounded-md bg-zinc-900 grid place-items-center">
          <Building2 className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="font-display font-bold text-3xl lg:text-4xl tracking-tight">Profilo azienda</h1>
          <p className="text-zinc-600">
            {isCommercialista
              ? "Come commercialista puoi modificare tutti i dati anagrafici delle aziende clienti"
              : "I dati fiscali sono bloccati per conformit\u00e0. Puoi modificare solo PEC, codice destinatario SDI e numerazione autofatture"}
          </p>
        </div>
      </div>

      {!isCommercialista && (
        <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-3" data-testid="company-lock-banner">
          <AlertCircle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
          <div className="flex-1 text-sm text-amber-900">
            <strong>Perch&eacute; i dati fiscali sono bloccati?</strong> Denominazione, P.IVA, codice fiscale, indirizzo e regime fiscale non possono essere modificati autonomamente per garantire la conformit&agrave; delle autofatture gi&agrave; emesse. Per modifiche contatta{" "}
            <a href="mailto:supporto@dasabo.com" className="font-semibold underline">supporto@dasabo.com</a>
          </div>
        </div>
      )}

      {isCommercialista && (
        <div className="mb-6 rounded-xl border border-blue-200 bg-blue-50 p-4 flex items-start gap-3" data-testid="commercialista-edit-banner">
          <AlertCircle className="w-5 h-5 text-blue-700 shrink-0 mt-0.5" />
          <div className="flex-1 text-sm text-blue-900">
            <strong>Modalit&agrave; Commercialista.</strong> Stai modificando i dati anagrafici del cliente attivo (visibile in alto). Cambia cliente dalla barra clienti sopra
          </div>
        </div>
      )}

      <form onSubmit={save} className="space-y-8" data-testid="company-form" noValidate>
        <section className="rounded-xl border border-[#E8E4D9] bg-white p-6">
          <h2 className="font-display font-semibold text-lg mb-4">
            Anagrafica
            {isCommercialista ? (
              <span className="ml-2 text-[10px] uppercase tracking-widest text-emerald-700 font-bold">Modificabile</span>
            ) : (
              <span className="ml-2 text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Solo lettura</span>
            )}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Denominazione / Nome" span={2}>
              <Input value={form.denominazione} onChange={upd("denominazione")} readOnly={!isCommercialista} disabled={!isCommercialista} className={!isCommercialista ? "bg-[#F5F2EA] text-zinc-600 cursor-not-allowed" : ""} testid="field-denominazione" />
            </Field>
            <Field label="Partita IVA">
              <Input value={form.partita_iva} onChange={upd("partita_iva")} readOnly={!isCommercialista} disabled={!isCommercialista} mono maxLength={11} className={!isCommercialista ? "bg-[#F5F2EA] text-zinc-600 cursor-not-allowed" : ""} testid="field-partita-iva" />
            </Field>
            <Field label="Codice Fiscale">
              <Input value={form.codice_fiscale || ""} onChange={upd("codice_fiscale")} readOnly={!isCommercialista} disabled={!isCommercialista} mono maxLength={16} className={!isCommercialista ? "bg-[#F5F2EA] text-zinc-600 cursor-not-allowed" : ""} testid="field-codice-fiscale" />
            </Field>
            <Field label="Regime fiscale" span={2}>
              {isCommercialista ? (
                <Select value={form.regime_fiscale} onChange={upd("regime_fiscale")} testid="field-regime-fiscale">
                  {REGIMI_FISCALI.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </Select>
              ) : (
                <Input value={form.regime_fiscale || ""} readOnly disabled className="bg-[#F5F2EA] text-zinc-600 cursor-not-allowed" testid="field-regime-fiscale" />
              )}
            </Field>
          </div>
        </section>

        <section className="rounded-xl border border-[#E8E4D9] bg-white p-6">
          <h2 className="font-display font-semibold text-lg mb-4">
            Sede legale
            {isCommercialista ? (
              <span className="ml-2 text-[10px] uppercase tracking-widest text-emerald-700 font-bold">Modificabile</span>
            ) : (
              <span className="ml-2 text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Solo lettura</span>
            )}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Indirizzo" span={2}>
              <Input value={form.indirizzo} onChange={upd("indirizzo")} readOnly={!isCommercialista} disabled={!isCommercialista} className={!isCommercialista ? "bg-[#F5F2EA] text-zinc-600 cursor-not-allowed" : ""} testid="field-indirizzo" />
            </Field>
            <Field label="CAP">
              <Input value={form.cap} onChange={upd("cap")} readOnly={!isCommercialista} disabled={!isCommercialista} mono maxLength={5} className={!isCommercialista ? "bg-[#F5F2EA] text-zinc-600 cursor-not-allowed" : ""} testid="field-cap" />
            </Field>
            <Field label="Comune">
              <Input value={form.comune} onChange={upd("comune")} readOnly={!isCommercialista} disabled={!isCommercialista} className={!isCommercialista ? "bg-[#F5F2EA] text-zinc-600 cursor-not-allowed" : ""} testid="field-comune" />
            </Field>
            <Field label="Provincia">
              {isCommercialista ? (
                <Select value={form.provincia} onChange={upd("provincia")} testid="field-provincia">
                  <option value="">— Seleziona provincia —</option>
                  {ITALIAN_PROVINCES.map((p) => (
                    <option key={p.code} value={p.code}>{p.code} — {p.name}</option>
                  ))}
                </Select>
              ) : (
                <Input value={form.provincia} readOnly disabled className="bg-[#F5F2EA] text-zinc-600 cursor-not-allowed" testid="field-provincia" />
              )}
            </Field>
            <Field label="Nazione">
              <Input value="IT" readOnly disabled mono className="bg-[#F5F2EA] text-zinc-600 cursor-not-allowed" testid="field-nazione" />
            </Field>
          </div>
        </section>

        <section className="rounded-xl border border-[#E8E4D9] bg-white p-6">
          <h2 className="font-display font-semibold text-lg mb-1">Contatti &amp; SDI <span className="ml-2 text-[10px] uppercase tracking-widest text-emerald-700 font-bold">Modificabile</span></h2>
          <p className="text-sm text-zinc-500 mb-4">Queste impostazioni le puoi cambiare in qualsiasi momento</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Telefono">
              <Input value={form.telefono || ""} onChange={upd("telefono")} testid="field-telefono" />
            </Field>
            <Field label="Email PEC">
              <Input type="email" value={form.email_pec || ""} onChange={upd("email_pec")} testid="field-pec" />
            </Field>
            <Field label="Codice destinatario SDI (7 caratteri)" span={2}>
              <Input value={form.codice_destinatario} onChange={upd("codice_destinatario")} mono maxLength={7} placeholder="0000000" testid="field-codice-destinatario" />
            </Field>
            <Field label="Aliquota IVA predefinita" span={2}>
              <select value={form.aliquota_iva_default ?? ""} onChange={upd("aliquota_iva_default")}
                data-testid="field-aliquota-default"
                className="w-full h-10 px-3 rounded-md border border-[#E8E4D9] bg-white text-sm focus:border-zinc-900 outline-none">
                <option value="">Nessuna (scelta manuale su ogni fattura)</option>
                <option value="22">22% — Ordinaria</option>
                <option value="10">10% — Ridotta</option>
                <option value="5">5% — Minima</option>
                <option value="4">4% — Agevolata</option>
                <option value="0">0% — Non imponibile / reverse charge</option>
              </select>
            </Field>
          </div>
        </section>

        <NumberingSection form={form} setForm={setForm} />

        <section className="rounded-xl border border-[#E8E4D9] bg-white p-6">
          <h2 className="font-display font-semibold text-lg mb-1">Email dedicata ricezione fatture</h2>
          <p className="text-sm text-zinc-600 mb-4">
            Ogni azienda ha un suo indirizzo email univoco. Inoltra qui i PDF ricevuti dai fornitori esteri: verranno elaborati e trasformati in autofatture automaticamente.
          </p>
          <div className="flex items-center gap-2 p-3 bg-[#F5F2EA] border border-[#E8E4D9] rounded-lg">
            <span className="font-mono text-sm text-zinc-800 flex-1 truncate" data-testid="company-inbound-email">
              {form?.inbound_email || "—"}
            </span>
            <button type="button" onClick={copyEmail} data-testid="company-copy-email"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm bg-white border border-[#E8E4D9] hover:bg-white transition-colors">
              <Copy className="w-3.5 h-3.5" /> Copia
            </button>
          </div>
          <p className="mt-2 text-[11px] text-zinc-500">
            L&apos;indirizzo &egrave; generato automaticamente dal nome della tua azienda e non &egrave; modificabile.
          </p>
        </section>

        <div className="flex items-center justify-between gap-4 flex-wrap">
          <button type="submit" disabled={saving} data-testid="company-save-btn"
            className="ml-auto inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-zinc-900 text-white text-sm font-medium hover:bg-zinc-800 disabled:opacity-70 transition-colors">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Salva impostazioni
          </button>
        </div>
      </form>
    </div>
  );
}


function NumberingSection({ form, setForm }) {
  const currentYear = String(new Date().getFullYear());
  const yy = currentYear.slice(-2);
  const sezionale = (form.sezionale || "AF01").trim() || "AF01";
  const counters = form.next_number_by_year || {};
  const nextNumber = parseInt(counters[currentYear], 10);
  const displayNext = Number.isFinite(nextNumber) && nextNumber > 0 ? nextNumber : 1;

  const setSezionale = (e) =>
    setForm((f) => ({ ...f, sezionale: e.target.value }));

  const setNextNumber = (e) => {
    const raw = e.target.value;
    const v = raw === "" ? 1 : Math.max(1, parseInt(raw, 10) || 1);
    setForm((f) => ({
      ...f,
      next_number_by_year: { ...(f.next_number_by_year || {}), [currentYear]: v },
    }));
  };

  return (
    <section className="rounded-xl border border-[#E8E4D9] bg-white p-6" data-testid="numbering-section">
      <h2 className="font-display font-semibold text-lg mb-1">Numerazione autofatture</h2>
      <p className="text-sm text-zinc-600 mb-4">
        Il numero di ogni autofattura si compone come <span className="font-mono font-semibold">Sezionale N°/Anno</span>
        {" "}(es. <span className="font-mono">AF01 1/26</span>). Il contatore riparte automaticamente da 1 ad ogni nuovo anno solare.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Sezionale" required>
          <Input
            value={form.sezionale ?? "AF01"}
            onChange={setSezionale}
            mono
            maxLength={20}
            placeholder="AF01"
            testid="field-sezionale"
          />
          <p className="mt-1 text-[11px] text-zinc-500">Prefisso che verrà stampato prima del progressivo. Default: <span className="font-mono">AF01</span>.</p>
        </Field>
        <Field label={`Prossimo numero (anno ${currentYear})`} required>
          <Input
            type="number"
            min="1"
            value={displayNext}
            onChange={setNextNumber}
            mono
            testid="field-next-number"
          />
          <p className="mt-1 text-[11px] text-zinc-500">
            Se stai migrando da un altro software di fatturazione elettronica e hai gi&agrave; emesso N autofatture quest&apos;anno, imposta qui il numero da cui ripartire.
          </p>
        </Field>
      </div>

      <div className="mt-4 rounded-lg border border-[#E8E4D9] bg-[#F5F2EA] p-4" data-testid="numbering-preview">
        <div className="text-[10px] font-semibold text-zinc-500 uppercase tracking-widest mb-1">Anteprima prossima autofattura</div>
        <div className="font-display font-extrabold text-2xl tracking-tight text-[#0A0A0A]">
          <span className="font-serif-accent text-[#1D4ED8]">#</span>
          <span className="font-mono">{sezionale} {displayNext}/{yy}</span>
        </div>
      </div>
    </section>
  );
}

function Field({ label, required, error, children, span = 1 }) {
  return (
    <div className={span === 2 ? "md:col-span-2" : ""}>
      <label className="flex items-center gap-1 text-xs font-medium text-zinc-700 mb-1">
        <span>{label}</span>
        {required && <span className="text-red-600">*</span>}
      </label>
      {children}
      {error && <div className="mt-1 text-xs text-red-600">{error}</div>}
    </div>
  );
}

function Input({ mono, invalid, testid, className = "", ...props }) {
  return (
    <input
      data-testid={testid}
      className={`w-full h-10 px-3 rounded-md border ${invalid ? "border-red-400 focus:border-red-500 focus:ring-red-500/10" : "border-[#E8E4D9] focus:border-zinc-900 focus:ring-zinc-900/10"} bg-white text-sm focus:ring-2 outline-none transition-colors ${mono ? "font-mono" : ""} ${className}`}
      {...props}
    />
  );
}

function Select({ invalid, testid, children, ...props }) {
  return (
    <select
      data-testid={testid}
      className={`w-full h-10 px-3 rounded-md border ${invalid ? "border-red-400" : "border-[#E8E4D9]"} bg-white text-sm focus:border-zinc-900 outline-none transition-colors`}
      {...props}
    >
      {children}
    </select>
  );
}


